import React, { createContext, useContext, useState, useEffect } from 'react';
import authService from '../services/authService.js';

// Create the authentication context
const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Auth provider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize authentication state on app start
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        setIsLoading(true);
        
        // Check if user is already authenticated
        const currentUser = authService.getCurrentUser();
        const authenticated = authService.isAuthenticated();
        
        if (authenticated && currentUser) {
          // Validate the session with the server
          const isValidSession = await authService.validateSession();
          
          if (isValidSession) {
            setUser(currentUser);
            setIsAuthenticated(true);
            
            // Optionally refresh user data from server
            try {
              const freshUserData = await authService.getCurrentUserInfo();
              setUser(freshUserData);
            } catch (error) {
              console.warn('Failed to refresh user data:', error);
              // Keep existing user data if refresh fails
            }
          } else {
            // Session is invalid, clear auth data
            authService.clearAuth();
            setUser(null);
            setIsAuthenticated(false);
          }
        } else {
          // Clear any stale data
          setUser(null);
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
        setIsInitialized(true);
      }
    };

    initializeAuth();
  }, []);

  // Listen for authentication required events
  useEffect(() => {
    const handleAuthenticationRequired = () => {
      // Trigger login modal when authentication is required
      if (window.openLoginModal) {
        window.openLoginModal();
      }
    };

    window.addEventListener('authenticationRequired', handleAuthenticationRequired);
    
    return () => {
      window.removeEventListener('authenticationRequired', handleAuthenticationRequired);
    };
  }, []);

  // Login function
  const login = async (username, password) => {
    try {
    //   setIsLoading(true);
      const userData = await authService.login(username, password);
      
      setUser(userData);
      setIsAuthenticated(true);
      
      // Retry any pending requests that failed due to authentication
      try {
        await authService.retryPendingRequests();
      } catch (error) {
        console.warn('Failed to retry some pending requests:', error);
      }
      
      // Dispatch login success event for backward compatibility
      window.dispatchEvent(new CustomEvent('loginSuccess'));
      
      return userData;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Logout function
  const logout = async () => {
    try {
      setIsLoading(true);
      await authService.logout();
      setUser(null);
      setIsAuthenticated(false);
      
      // Dispatch logout event for backward compatibility
      window.dispatchEvent(new CustomEvent('logoutSuccess'));
    } catch (error) {
      console.error('Logout error:', error);
      // Ensure logout happens even if there's an error
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Refresh user data
  const refreshUser = async () => {
    try {
      if (isAuthenticated) {
        const userData = await authService.getCurrentUserInfo();
        setUser(userData);
        return userData;
      }
    } catch (error) {
      console.error('Failed to refresh user:', error);
      // If refresh fails, user might be logged out
      await logout();
      throw error;
    }
  };

  // Check if user has specific permissions/roles
  const hasRole = (role) => {
    return user?.roles?.includes(role) || false;
  };

  // Check if user has any of the specified roles
  const hasAnyRole = (roles) => {
    if (!user?.roles) return false;
    return roles.some(role => user.roles.includes(role));
  };

  // Get authentication headers for API requests
  const getAuthHeaders = () => {
    return authService.getAuthHeaders();
  };

  // Make authenticated request
  const authenticatedRequest = async (url, options = {}) => {
    return authService.authenticatedRequest(url, options);
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    isInitialized,
    login,
    logout,
    refreshUser,
    hasRole,
    hasAnyRole,
    getAuthHeaders,
    authenticatedRequest
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
